import React from "react";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import ProfileHeader from "../../components/profile/ProfileHeader.tsx";
import { useParams } from "react-router-dom";
import { notification } from "../../utils/Notifications.tsx";
import { fetchPartnerById } from "../../services/application.service.ts";
import { Partner } from "../../models/Partner.ts";
import S3Service from "../../services/s3.service.ts";
import TimeLineDocuments from "../../components/profile/TimeLineDocuments.tsx";
import InformationUser from "../../components/profile/InformationUser.tsx";
import InformationPosition from "../../components/profile/InformationPosition.tsx";
import Contracts from "../../components/profile/Contracts.tsx";
import Layout from "../../components/Layout.tsx";
import { useLocation } from "react-router-dom";
import ConfettiExplosion, { ConfettiProps } from "react-confetti-explosion";
import { useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import AccountantContract from "../../components/profile/AccountantContract.tsx";
import Movimientos from "../../components/profile/Movimientos.tsx";
import Tracking from "../../components/profile/Tracking.tsx";
import ProfilePartnerHeroCard from "../../components/profile/ProfilePartnerHeroCard.tsx";
import ProfilePersonalSummary from "../../components/profile/ProfilePersonalSummary.tsx";
import ProfilePartnerPanelSummary from "../../components/profile/ProfilePartnerPanelSummary.tsx";
import { useProfilePartnerLayoutStyles } from "../../components/profile/useProfilePartnerLayoutStyles.ts";
import { PROFILE_DEFAULT_TAB } from "../../components/profile/profileTabUtils.ts";

const ProfilePartner = () => {
  useAppSettings();
  useProfilePartnerLayoutStyles();
  const { user } = useSelector((state: RootState) => state.auth);
  const location = useLocation();
  const confetti = location.state?.confetti;
  const { id } = useParams();
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [partner, setPartner] = React.useState<Partner>({} as Partner);
  const [files, setFiles] = React.useState({
    profile: "",
  });
  const [activeTabId, setActiveTabId] = React.useState(PROFILE_DEFAULT_TAB);

  const fetchPartner = async () => {
    try {
      const response = await fetchPartnerById(Number(id));
      setPartner(response);
      const profileUrl = await S3Service.getFile(
        "PERFIL_" + response.person.curp
      );
      setFiles({ profile: profileUrl.url });
    } catch (error) {
      setError(error?.message);
      notification(
        "Error",
        "Ocurrió un error al recuperar al colaborador",
        "danger"
      );
    } finally {
      setLoading(false);
    }
  };

  const largeProps: ConfettiProps = {
    force: 0.8,
    duration: 3000,
    particleCount: 300,
    width: 1600,
    colors: ["#041E43", "#1471BF", "#5BB4DC", "#FC027B", "#66D805"],
  };

  React.useEffect(() => {
    fetchPartner();
  }, []);

  if (error) return <>error</>;

  return (
    <Layout loading={loading}>
      {!loading && partner?.person && (
        <div id="content" className="app-content p-0">
          <div className="profile-content profile-content--partner p-0">
            <div className="profile-partner-layout">
              <div className="profile-partner-layout__left">
                <div className="profile-partner-identity-card">
                  <ProfilePartnerHeroCard
                    partner={partner}
                    profileUrl={files.profile}
                  />
                  <ProfilePartnerPanelSummary
                    partner={partner}
                    placement="identity"
                  />
                </div>
                <ProfilePersonalSummary
                  partner={partner}
                  onEdit={() => setActiveTabId("informacion")}
                />
              </div>

              <div className="profile-partner-layout__right">
                <div className="profile-partner-panel">
                  <ProfilePartnerPanelSummary
                    partner={partner}
                    placement="panel"
                  />
                  <ProfileHeader
                    partner={partner}
                    activeTabId={activeTabId}
                    onTabSelect={setActiveTabId}
                  />
                  <div className="profile-partner-panel__body">
                    {activeTabId === "expediente" && (
                      <TimeLineDocuments
                        partner={partner}
                        reloadPage={fetchPartner}
                        embedded
                      />
                    )}
                    {activeTabId === "informacion" && (
                      <InformationUser
                        partner={partner}
                        reloadPage={() => fetchPartner()}
                        embedded
                      />
                    )}
                    {activeTabId === "tracking" &&
                      (user.rol === "Administrador" ||
                        user.rol === "Administrador-" ||
                        user.rol === "Contador") && (
                        <Tracking
                          partner={partner}
                          reloadPage={fetchPartner}
                          embedded
                        />
                      )}
                    {activeTabId === "contrato" &&
                      (user.rol === "Trabajador" ||
                        user.rol === "Administrador" ||
                        user.rol === "Administrador-") && (
                        <Contracts partner={partner} embedded />
                      )}
                    {activeTabId === "contratoIMSS" &&
                      (user.rol === "Contador" ||
                        user.rol === "Administrador" ||
                        user.rol === "Administrador-") && (
                        <AccountantContract partner={partner} embedded />
                      )}
                    {activeTabId === "empleo" && (
                      <InformationPosition
                        partner={partner}
                        reloadPage={() => fetchPartner()}
                        embedded
                      />
                    )}
                    {activeTabId === "movimientos" &&
                      user.rol !== "Lider de sucursal" && (
                        <Movimientos
                          partner={partner}
                          reloadPage={() => fetchPartner()}
                          embedded
                        />
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {confetti && <ConfettiExplosion {...largeProps} />}
    </Layout>
  );
};

export default ProfilePartner;
